import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import {
  LucideAngularModule,
  Home,
  User,
  FileText,
  Calendar,
  Settings,
  LogIn,
  X,
} from 'lucide-angular';
import { environment } from '../../../../../environments';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { catchError, timeout, takeUntil } from 'rxjs/operators';
import { ReferentielService } from '../../../../core/services/referentiel.service';
import { Country } from '../../../../core/interfaces/doctor.interface';
import { CountryStorageService } from '../../../../core/services/country.storage.service';
import { AuthService } from '../../../../core/services/auth.service';
import { ThemeService, Theme } from '../../../../core/services/theme.service';
import { Subject } from 'rxjs';

interface Result {
  id: string;
  result: string;
  name?: string;
  type: string;
}

interface QueryResult {
  id: string;
  result: string;
  type: 'doctor' | 'structure' | 'pharmacy' | 'speciality';
}

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LucideAngularModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
  ],
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit, OnDestroy {
  readonly HomeIcon = Home;
  readonly UserIcon = User;
  readonly FileTextIcon = FileText;
  readonly CalendarIcon = Calendar;
  readonly SettingsIcon = Settings;
  readonly LogInIcon = LogIn;
  readonly XIcon = X;
  selectedCountry: Country | null = null;
  PrefixUrlCountryCode: string | null | undefined = null;

  searchForm: FormGroup;

  connectedUser = false;

  features = [
    {
      icon: this.UserIcon,
      title: 'Gestion des Profils',
      description: 'Créez et gérez vos profils médicaux de manière sécurisée',
    },
    {
      icon: this.FileTextIcon,
      title: 'Documents Médicaux',
      description:
        'Stockez et organisez tous vos documents médicaux importants',
    },
    {
      icon: this.CalendarIcon,
      title: 'Rendez-vous',
      description: 'Planifiez et suivez vos rendez-vous médicaux',
    },
    {
      icon: this.SettingsIcon,
      title: 'Paramètres Avancés',
      description: 'Personnalisez votre expérience selon vos besoins',
    },
  ];

  stats = [
    { number: '10K+', label: 'Utilisateurs' },
    { number: '50K+', label: 'Documents' },
    { number: '99.9%', label: 'Disponibilité' },
    { number: '24/7', label: 'Support' },
  ];

  query: any;
  selectedQuery: any;
  town: any;
  selectedTown: any;

  towns: Result[] = [];
  results: Result[] = [];

  townLoading = false;
  queryLoading = false;

  isQuerySelected = false;
  isTownSelected = false;

  /** Catégorie cible de la recherche (utilisée par le bouton Rechercher) */
  selectedSearchCategory: 'medecins' | 'structures' | 'pharmacies' = 'medecins';

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private referentielService: ReferentielService,
    private router: Router,
    private route: ActivatedRoute,
    private countryStorage: CountryStorageService,
    private authService: AuthService,
    private themeService: ThemeService,
  ) {
    this.searchForm = this.fb.group({
      town: [''],
      query: [''],
    });
  }

  ngOnInit() {
    this.onSearchTown();
    this.onSearchQuery();

    this.countryStorage.country$
      .pipe(takeUntil(this.destroy$))
      .subscribe((country) => {
        this.selectedCountry = country;
        this.PrefixUrlCountryCode = `/${this.selectedCountry?.code?.toLowerCase()}`;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchQuery() {
    const queryControl = this.searchForm.get('query');
    if (queryControl) {
      queryControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((term) => {
            if (this.isQuerySelected) {
              this.isQuerySelected = false;
              return of([]);
            }
            if (term == null || typeof term !== 'string') {
              return of([]);
            }
            const trimmed = (term as string).trim();
            if (!trimmed) {
              this.results = [];
              this.selectedQuery = null;
              return of([]);
            }
            this.queryLoading = true;
            return this.referentielService
              .searchDoctorsWithSpecialities(trimmed)
              .pipe(
                timeout(120000),
                catchError(() => of([])),
              );
          }),
          takeUntil(this.destroy$),
        )
        .subscribe((res) => {
          this.queryLoading = false;
          this.results = Array.isArray(res) ? res : [];
        });
    }
  }

  onSearchTown() {
    const townControl = this.searchForm.get('town');
    if (townControl) {
      townControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((term) => {
            if (this.isTownSelected) {
              this.isTownSelected = false;
              return of([]);
            }
            if (term == null || typeof term !== 'string') {
              return of([]);
            }
            const trimmed = (term as string).trim();
            if (!trimmed) {
              this.towns = [];
              this.selectedTown = null;
              return of([]);
            }
            this.townLoading = true;
            return this.referentielService.townSearchAutocomplete(trimmed).pipe(
              timeout(120000),
              catchError(() => of([])),
            );
          }),
          takeUntil(this.destroy$),
        )
        .subscribe((res) => {
          this.townLoading = false;
          this.towns = Array.isArray(res) ? res : [];
        });
    }
  }

  /** Indique si le champ ville a une valeur (objet ou chaîne non vide). */
  get hasTownValue(): boolean {
    const v = this.searchForm.get('town')?.value;
    if (v == null) return false;
    if (typeof v === 'string') return (v as string).trim() !== '';
    return true;
  }

  /** Indique si le champ prestataire a une valeur (objet ou chaîne non vide). */
  get hasQueryValue(): boolean {
    const v = this.searchForm.get('query')?.value;
    if (v == null) return false;
    if (typeof v === 'string') return (v as string).trim() !== '';
    return true;
  }

  clearTown(): void {
    this.searchForm.patchValue({ town: '' }, { emitEvent: false });
    this.towns = [];
    this.selectedTown = null;
  }

  clearQuery(): void {
    this.searchForm.patchValue({ query: '' }, { emitEvent: false });
    this.results = [];
    this.selectedQuery = null;
  }

  // Separate display functions for each autocomplete
  displayTownFn(item: Result | null): string {
    return item?.result || '';
  }

  displayQueryFn(item: Result | string | null): string {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    return item.type === 'speciality' ? item.name || '' : item.result || '';
  }

  // Add selection handlers
  onQuerySelected(event: any): void {
    const selectedQuery = event.option.value as QueryResult;

    if (!selectedQuery || !selectedQuery.type) {
      return;
    }

    switch (selectedQuery.type) {
      case 'doctor':
        this.router.navigate([
          `/${this.selectedCountry?.code?.toLocaleLowerCase()}/medecins/${selectedQuery.id}`,
        ]);
        break;
      case 'structure':
        this.router.navigate([
          `/${this.selectedCountry?.code?.toLocaleLowerCase()}/structures/${selectedQuery.id}`,
        ]);
        break;
      case 'pharmacy':
        this.router.navigate([
          `/${this.selectedCountry?.code?.toLocaleLowerCase()}/pharmacies/${selectedQuery.id}`,
        ]);
        break;
      case 'speciality':
        // Don't navigate immediately for speciality type
        this.isQuerySelected = true;
        break;
      default:
        console.warn('Unknown query type:', selectedQuery.type);
    }
  }

  onSearch(): void {
    const { query, town } = this.searchForm.value;
    const code = this.selectedCountry?.code?.toLowerCase();
    const baseUrl = `/${code}/${this.selectedSearchCategory}`;

    const queryParams: Record<string, string | number | null | undefined> = {};

    if (town && typeof town === 'object' && town.id) {
      queryParams['townId'] = town.id;
    } else {
      queryParams['townId'] = null;
    }

    if (query && typeof query === 'object' && query.type === 'speciality') {
      queryParams['specialisationIds'] = query.id;
      queryParams['query'] = null;
    } else if (typeof query === 'string' && query.trim().length > 0) {
      queryParams['query'] = query.trim();
      queryParams['specialisationIds'] = null;
    } else {
      queryParams['query'] = null;
      queryParams['specialisationIds'] = null;
    }

    this.router.navigate([baseUrl], {
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  getDossierPatientLink(): string {
    return this.isLoggedIn()
      ? `${this.PrefixUrlCountryCode}/passport`
      : `${this.PrefixUrlCountryCode}/register`;
  }

  /**
   * Obtient le thème actuel
   */
  getCurrentTheme(): Theme {
    return this.themeService.getCurrentTheme();
  }

  /**
   * Nom du produit selon le thème (FA'JMA ou Passeport Médical)
   */
  getProductName(): string {
    return this.themeService.getProductName();
  }

  /**
   * Détermine si on doit utiliser le style simple (comme DPP) ou complexe
   */
  useSimpleLandingStyle(): boolean {
    const theme = this.getCurrentTheme();
    return theme === 'dpp';
  }

  /**
   * Obtient la classe CSS pour les boutons de recherche selon le thème
   */
  selectSearchCategory(
    category: 'medecins' | 'structures' | 'pharmacies',
  ): void {
    this.selectedSearchCategory = category;
  }

  /** Déclenche la recherche à la touche Entrée (évite soumission form + lance onSearch) */
  onSearchKeyDown(event: Event): void {
    const ev = event as KeyboardEvent;
    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.onSearch();
    }
  }

  getSearchButtonClass(): string {
    const theme = this.getCurrentTheme();

    if (theme === 'dpp') {
      return 'btn-primary'; // Utilise la couleur primaire du thème
    }

    // Pour les autres thèmes, utiliser la couleur primaire du thème
    return 'btn-search-primary';
  }

  /**
   * Obtient la classe CSS pour les boutons "Que recherchez-vous ?" selon le thème et la route
   */
  getCategoryButtonClass(
    category: 'medecins' | 'structures' | 'pharmacies',
  ): string {
    const theme = this.getCurrentTheme();
    const currentUrl = this.router.url;

    // Vérifier si on est sur la landing page (home)
    const isLandingPage =
      currentUrl.includes('/home') ||
      currentUrl === '/' ||
      (currentUrl.split('/').length === 2 &&
        !currentUrl.includes('/medecins') &&
        !currentUrl.includes('/structures') &&
        !currentUrl.includes('/pharmacies'));

    // Sur la landing page, la catégorie sélectionnée détermine le style
    if (isLandingPage) {
      if (theme === 'dpp') {
        return 'btn-category-primary'; // Tous les boutons utilisent la couleur primaire pour DPP
      }
      return category === this.selectedSearchCategory
        ? 'btn-category-primary'
        : 'btn-category-secondary';
    }

    // Sur les autres pages, vérifier si la catégorie correspond à la route actuelle
    const isActive = currentUrl.includes(`/${category}`);

    if (theme === 'dpp') {
      return 'btn-category-primary'; // Tous les boutons utilisent la couleur primaire pour DPP
    }

    // Pour les autres thèmes, utiliser btn-category-primary pour le bouton actif
    // et btn-category-secondary pour les autres
    return isActive ? 'btn-category-primary' : 'btn-category-secondary';
  }
}
